import { useEffect } from "react";
import polylabel from "polylabel";

/**
 * Hook for managing pickup market toggle checkboxes and click handling
 * @param {Object} options
 * @param {boolean} options.isMapReady - Whether the map is ready
 * @param {React.RefObject} options.mapRef - Reference to MarketMapBase
 * @param {Object} options.pickupLocation - Pickup location data from Redux
 * @param {string[]} options.pickupMarkets - List of pickup market names
 * @param {string[]} options.excludedPickupMarkets - List of excluded market names
 * @param {string} options.mapViewMode - Current view mode ("fullMap" | "market")
 * @param {Function} options.onTogglePickupMarket - Callback when a market is toggled
 */
export const useMarketToggles = ({
  isMapReady,
  mapRef,
  pickupLocation,
  pickupMarkets,
  excludedPickupMarkets,
  mapViewMode,
  onTogglePickupMarket,
}) => {
  // Add toggle icons on pickup markets when in market mode
  useEffect(() => {
    if (!isMapReady || !mapRef.current) return;

    const mapInstance = mapRef.current;
    const map = mapInstance.getMap();
    const markets = mapInstance.getMarkets();

    if (!map || !markets?.data) return;

    const showToggles = pickupMarkets.length > 1 && mapViewMode === "market";

    // Remove existing layer and source
    if (map.getLayer("pickup-market-toggles-layer")) {
      map.removeLayer("pickup-market-toggles-layer");
    }
    if (map.getSource("pickup-market-toggles")) {
      map.removeSource("pickup-market-toggles");
    }

    if (!showToggles) return;

    // Add checkbox images if not already added
    if (!map.hasImage("market-toggle-unchecked")) {
      const uncheckedSvg = `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="0.5" y="0.5" width="15" height="15" rx="1.5" fill="#F1F3F3" stroke="#2E5D4B"/>
            </svg>`;
      const uncheckedImg = new Image(16, 16);
      uncheckedImg.onload = () => map.addImage("market-toggle-unchecked", uncheckedImg);
      uncheckedImg.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(uncheckedSvg);
    }

    if (!map.hasImage("market-toggle-checked")) {
      const checkedSvg = `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="16" height="16" rx="2" fill="#2E5D4B"/>
                <path d="M4 8L7 11L13 5" stroke="#E9F8F3" stroke-width="1.5" stroke-linecap="round"/>
            </svg>`;
      const checkedImg = new Image(16, 16);
      checkedImg.onload = () => map.addImage("market-toggle-checked", checkedImg);
      checkedImg.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(checkedSvg);
    }

    const mainPickupMarket = pickupLocation.data?.market?.city;
    const features = pickupMarkets
      .filter((marketName) => marketName !== mainPickupMarket)
      .map((marketName) => {
        const marketFeature = markets.data.features.find((f) => f.properties.marketName === marketName);
        if (marketFeature) {
          const coords = marketFeature.geometry.coordinates;
          const labelCoords = polylabel(coords, 0.001);
          const isExcluded = excludedPickupMarkets.includes(marketName);
          return {
            type: "Feature",
            geometry: {
              type: "Point",
              coordinates: [labelCoords[0], labelCoords[1]],
            },
            properties: {
              marketName,
              isExcluded,
              icon: isExcluded ? "market-toggle-unchecked" : "market-toggle-checked",
            },
          };
        }
        return null;
      })
      .filter(Boolean);

    map.addSource("pickup-market-toggles", {
      type: "geojson",
      data: {
        type: "FeatureCollection",
        features,
      },
    });

    map.addLayer({
      id: "pickup-market-toggles-layer",
      type: "symbol",
      source: "pickup-market-toggles",
      layout: {
        "icon-image": ["get", "icon"],
        "icon-size": ["interpolate", ["linear"], ["zoom"], 4, 0.8, 6, 1.2, 8, 1.8, 10, 2.5],
        "icon-allow-overlap": true,
      },
    });

    // Ensure overlay layers stay on top (toggle layer should be topmost)
    mapInstance.moveOverlayLayersToTop();
  }, [isMapReady, pickupMarkets, excludedPickupMarkets, mapViewMode, pickupLocation.data]);

  // Handle click on pickup markets to toggle exclusion
  useEffect(() => {
    if (!isMapReady || !mapRef.current) return;

    const map = mapRef.current.getMap();
    if (!map) return;

    const mainPickupMarket = pickupLocation.data?.market?.city;

    const handleMarketClick = (e) => {
      if (pickupMarkets.length <= 1 || mapViewMode !== "market") return;

      const features = map.queryRenderedFeatures(e.point, { layers: ["markets-layer"] });
      if (features.length > 0) {
        const marketName = features[0].properties.marketName;
        if (pickupMarkets.includes(marketName) && marketName !== mainPickupMarket) {
          onTogglePickupMarket(marketName);
        }
      }
    };

    map.on("click", "markets-layer", handleMarketClick);

    return () => {
      map.off("click", "markets-layer", handleMarketClick);
    };
  }, [isMapReady, pickupMarkets, mapViewMode, onTogglePickupMarket, pickupLocation.data]);
};
